import { z } from 'zod';
import { openItem } from './ledger';
import { knowledgeEntry } from './knowledge';
import { captureHealth, captureReason, isoDate, uuid } from './common';

export const calendarStatus = z.object({
  connected: z.boolean(),
  /** Recall's own word: connecting · connected · disconnected. Null when never connected. */
  status: z.string().nullable(),
  lastSyncedAt: isoDate.nullable(),
  /**
   * Whether an invited group is read as the people in it (PRD 38). False on
   * every calendar connected before 2026-09-19, because the scope did not
   * exist then and Google only grants one by asking — which is why the card
   * says so rather than letting a meeting quietly list two people out of eight.
   */
  expandsGroups: z.boolean(),
});
export type CalendarStatus = z.infer<typeof calendarStatus>;

/** The dispatcher's own words for not sending a bot, so a row can say them. */
export const dispatchRefusal = z.enum([
  'no-meeting-url',
  'unsupported-platform',
  'turned-off-for-this-series',
  'already-dispatched',
  'in-the-past',
  'too-far-ahead',
  'nobody-else-invited',
  'one-to-ones-off',
  'turned-off-for-this-meeting',
  'longer-than-a-meeting',
  /** Its owner records only the meetings they turn on, and nobody turned this one on. */
  'not-turned-on',
  /** Recordable, but nobody in it may record any more (the free month, `access/`). */
  'free-month-ended',
]);
export type DispatchRefusal = z.infer<typeof dispatchRefusal>;

/**
 * One row of the week ahead: when, what, whether it will be recorded, and —
 * once a bot exists — what that bot is doing. `plan` is the dispatcher's verdict
 * as of now (`too-far-ahead` reads as WILL_RECORD: the sweep sends it in time);
 * `capture` is the bot's state and null until there is one.
 */
export const upcomingEvent = z.object({
  id: uuid,
  title: z.string(),
  startsAt: isoDate,
  endsAt: isoDate.nullable(),
  meetingUrl: z.string().nullable(),
  platform: z.string().nullable(),
  seriesKey: z.string().nullable(),
  organizerEmail: z.string().nullable(),
  attendees: z.array(z.string()),
  plan: z.union([z.literal('WILL_RECORD'), dispatchRefusal]),
  /** A person's answer for this one meeting: true records, false skips, null follows the series. */
  recordOverride: z.boolean().nullable(),
  capture: captureHealth.nullable(),
  /** Why nothing was recorded, once `capture` is FAILED (2026-10-08). */
  captureReason: captureReason.nullable(),
  botRunId: uuid.nullable(),
  meetingId: uuid.nullable(),
  /**
   * On this person's own calendar. False: a meeting they are invited to that a
   * colleague's calendar brought in — one meeting is one row, so their own copy
   * is never stored. Listed so it can be found and opened; recording it or not
   * is decided on the calendar it came from.
   */
  mine: z.boolean(),
});
export type UpcomingEvent = z.infer<typeof upcomingEvent>;

/** The longest window the calendar reads at once: six weeks (PRD 67). */
export const CALENDAR_WINDOW_DAYS = 42;

export const upcomingEventsQuery = z
  .object({
    days: z.coerce.number().int().min(1).max(31).default(7),
    /**
     * A window of the calendar, past included (PRD 67): both or neither, and
     * at most six weeks. Without them, the hour just gone and `days` ahead.
     */
    from: isoDate.optional(),
    to: isoDate.optional(),
  })
  .refine((q) => (q.from === undefined) === (q.to === undefined), 'from and to go together')
  .refine(
    (q) =>
      !q.from ||
      !q.to ||
      (Date.parse(q.to) > Date.parse(q.from) &&
        Date.parse(q.to) - Date.parse(q.from) <= CALENDAR_WINDOW_DAYS * 24 * 60 * 60_000),
    'to comes after from, at most six weeks later',
  );
export type UpcomingEventsQuery = z.infer<typeof upcomingEventsQuery>;

/** "Skip just this one" (false), "Record this one" (true), or back to the series (null). */
export const recordOverrideBody = z.object({ record: z.boolean().nullable() });
export type RecordOverrideBody = z.infer<typeof recordOverrideBody>;

/**
 * Before a meeting (PRD 30 §6, "briefs get real"): what is open going in,
 * what was decided last time, what we know about the people and their
 * companies, and who is coming — composed at read time from rows this
 * person can already open, never from a model.
 */
export const eventBrief = z.object({
  event: z.object({
    id: uuid,
    title: z.string(),
    startsAt: isoDate,
    endsAt: isoDate.nullable(),
    seriesKey: z.string().nullable(),
    seriesTitle: z.string().nullable(),
    plan: upcomingEvent.shape.plan,
    /**
     * What the notetaker is doing, and the run it is doing it on — so the
     * brief can stop or pause it (PRD 40). Until 2026-09-20 the brief was the
     * only page that existed while a meeting was being recorded, and it was
     * the one page with no way to act on it: the meeting's own page does not
     * exist until the recording has been ingested.
     */
    capture: captureHealth.nullable(),
    botRunId: uuid.nullable(),
    meetingId: uuid.nullable(),
    /**
     * Whether there is anywhere for a notetaker to go — a link, on a platform
     * a bot can join. Not "should we send one": that is the dispatcher's
     * question, and this button exists precisely for when the answer to it was
     * no (PRD 42).
     */
    canSendBot: z.boolean(),
  }),
  attendees: z.array(
    z.object({
      email: z.string(),
      name: z.string().nullable(),
      company: z.string().nullable(),
      guest: z.boolean(),
      lastMetAt: isoDate.nullable(),
      meetingsTogether: z.number().int(),
      owesYou: z.number().int(),
      youOwe: z.number().int(),
    }),
  ),
  lastTime: z
    .object({
      meetingId: uuid,
      title: z.string(),
      startedAt: isoDate,
      summary: z.array(z.string()),
      decisions: z.array(z.object({ id: uuid, text: z.string(), status: z.string() })),
    })
    .nullable(),
  open: z.array(openItem),
  knowledge: z.array(knowledgeEntry),
});
export type EventBrief = z.infer<typeof eventBrief>;
